import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'

// The reminder bar at the top of the app body (docs/roadmap/hellobars.md).
//
// Rails resolves the bar on every page render and hands it over as
// window.HELLOBAR, so there is no fetch here: the store only reports what the
// user did with the bar that page shipped.
function post(action, key) {
  const { axiosRequest } = useRequest()
  return axiosRequest('POST', `/api/v1/hellobar/${action}`, { key }, false)
}

// "Preview on site" from /admin/hellobar. The unsaved draft lives only in this
// browser's localStorage, so nobody else sees it and nothing is written server
// side. It expires on its own so a forgotten preview does not linger.
export const PREVIEW_STORAGE_KEY = 'hellobar_preview_draft'
const PREVIEW_TTL = 60 * 60 * 1000 // 1 hour

export function savePreviewDraft({ html, text, color, emoji }) {
  try {
    localStorage.setItem(PREVIEW_STORAGE_KEY, JSON.stringify({
      html, text, color, emoji, expiresAt: Date.now() + PREVIEW_TTL,
    }))
  } catch {
    // Private mode or storage disabled: the preview simply does not open.
  }
}

export function clearPreviewDraft() {
  try {
    localStorage.removeItem(PREVIEW_STORAGE_KEY)
  } catch {
    // Nothing to clear.
  }
}

export function readPreviewDraft() {
  try {
    const raw = localStorage.getItem(PREVIEW_STORAGE_KEY)
    if (!raw) return null
    const draft = JSON.parse(raw)
    if (!draft.expiresAt || draft.expiresAt < Date.now()) {
      clearPreviewDraft()
      return null
    }
    return draft
  } catch {
    return null
  }
}

function previewBarFrom(draft) {
  if (!draft) return null
  return {
    key: 'preview',
    kind: 'admin',
    priority: 1,
    tone: draft.color,
    icon: 'megaphone',
    emoji: draft.emoji || null,
    title: null,
    body: draft.text || 'Your message will appear here.',
    body_html: draft.html || null,
    cta: null,
    dismissible: true,
  }
}

export const useHellobarStore = defineStore('hellobar', {
  state: () => ({
    bar: null,
    previewBar: null,
    hidden: false,
    // Keys already reported as shown. Pinia outlives the Vue app that Turbo
    // remounts on every visit, so this keeps one impression per full page load
    // instead of one per navigation. The server dedupes per snooze cycle too.
    reportedKeys: [],
  }),

  getters: {
    previewing: (state) => !!state.previewBar,
    currentBar: (state) => state.previewBar || state.bar,
    visible() {
      return !!this.currentBar && !this.hidden
    },
  },

  actions: {
    // Always take the freshly rendered payload: the bar may have changed (or
    // been snoozed from another tab) since the previous visit.
    hydrate() {
      this.bar = window.HELLOBAR || null
      this.hidden = false
      this.refreshPreview()
    },

    // Only super admins can preview. The draft is re-read when the admin page
    // in another tab edits it (AppHellobar listens for the storage event).
    refreshPreview() {
      this.previewBar = window.CURRENT_USER?.super_admin ? previewBarFrom(readPreviewDraft()) : null
    },

    exitPreview() {
      clearPreviewDraft()
      this.previewBar = null
    },

    async recordImpression() {
      if (this.previewing) return
      const key = this.bar?.key
      if (!key || this.reportedKeys.includes(key)) return
      this.reportedKeys.push(key)
      await post('impression', key)
    },

    // Optimistic: the bar goes away immediately. If the request fails it stays
    // hidden for this page; popping a closed bar back open is worse than
    // showing it again on the next page load. Closing a preview only ends it.
    async dismiss() {
      if (this.previewing) {
        this.exitPreview()
        return
      }
      const key = this.bar?.key
      if (!key) return
      this.hidden = true
      const res = await post('dismiss', key)
      if (res.js_error || !res.response?.success) {
        console.warn('[hellobar] dismiss was not saved', res)
      }
    },

    async recordClick() {
      if (this.previewing) return
      const key = this.bar?.key
      if (!key) return
      await post('click', key)
    },
  },
})
